/**
 * SHA-256 hex digest of a trimmed/lowercased email, used as a stable per-email
 * RUM token for unique-email counts and metric slicing without logging the raw
 * address. PRIVACY: a plain unkeyed SHA-256 of an email is reversible (the input
 * space is enumerable) and thus still PII — logged per team decision on a
 * restricted RUM store; see the owning CR for the data-classification sign-off.
 */
export async function hashEmail(email: string): Promise<string | undefined> {
  // Guard missing WebCrypto (non-secure contexts) so it degrades to no token.
  if (
    typeof crypto === 'undefined' ||
    !crypto.subtle ||
    typeof crypto.subtle.digest !== 'function'
  ) {
    return undefined;
  }
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  try {
    const data = new TextEncoder().encode(normalized);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Never let hashing failure block the user's login.
    return undefined;
  }
}
